/**
 * Settings' address (typed search params, CD30). Every param but `section` is optional, and anything unrecognised is
 * dropped, so a hand-edited or stale link can never break the page.
 *
 * - `section`: the open section (any id, or the old `profile`; the page opens Profile for anything it doesn't show).
 * - `card`: a door that names a card. Your numbers: `target` = Who we look for, `tiers` = Your tiers (A1): the card
 *   comes to the top of the column once the section is on screen. Draper Knowledge Base: `company-rules`,
 *   `product-facts`, `wording` (Wording by country): it lands as a search result does — 72px under the top of what she
 *   reads, glowing, with the focus on its heading — after the section leaves any document it had open (F1).
 *   Team & roles: `agencies` (Campaign details' Approvals card, and any link to the agencies) lands on the Agencies
 *   card the same way, or with `agency` on one agency's row. Integrations: `approvals` (Campaign details opened from
 *   Approvals in Slack's campaign names leads back here) lands on Approvals in Slack the same way.
 * - `country`: with `card=wording` — one of the 19 countries (Campaign details' "Change ›" names its campaign's
 *   first): the door lands on that country's row, glowing, with the focus on its Change (the card, when the row isn't there).
 *   With `card=company-rules` (round 4's F1: Wording by country's "Add a rule for Australia ›"), Company rules' box opens
 *   on Only some countries with that country chosen, and the door lands on the box.
 * - `edit`: `true`, with `card=wording` and a `country` only (round 4's F1: Campaign details' "Change ›"): that country's
 *   row opens in its editor, with the focus on the level she has — for a seat that may change wording; any other lands on
 *   the row as without it.
 * - `document`: with `card=product-facts` only — a product facts document's id (a uuid; a Wrong claim row's link names
 *   the fact's document): the door opens that document's See the facts (the card, when it is no longer listed).
 * - `fact`: with `card=product-facts` and a `document` only — one of that document's facts (its id; a Wrong claim row's
 *   link names the fact the claim was checked against, round 3's R5): See the facts lands on it, glowing, with the focus
 *   on it (at its title, as before, when it isn't listed).
 * - `campaign`: with `card=company-rules` only — one campaign's program id (Campaign details' "Add a rule for this
 *   campaign ›", round 3's C1): Company rules' box opens on Only one campaign with that campaign.
 * - `agency`: with `card=agencies` only — one agency's id (a whole positive number; Review Content's "Sent by
 *   Wavemaker" leads to Wavemaker's people): the door lands on that agency's row, glowing, with the focus on its name;
 *   on the card when the row isn't there.
 * - `rule`: with `card=company-rules` only — one company rule's id (a uuid; Review Content's rule line, round 4's F1): the
 *   door lands on that rule, glowing, with the focus on its Change (on its words, for a seat that can't change it); on
 *   the card when it is no longer listed.
 * - `back`: the page she came from, for Settings' own way back ("Back to Review Content", "Back to Campaign details",
 *   "Back to Home", "Back to Artifacts", "Back to Deals"): only a Review Content address (`/review/content…`, the draft
 *   she was on), Campaign details' door (`/operations?playbook=<id>&details=true`, with its own `card` and way back when
 *   it has them, so Review Content → Campaign details → Team & roles → Back → Back reaches the draft; and with
 *   `&from=change|add|also` for the control it refocuses), Home's Brief changes on one suggestion
 *   (`/?brief=<uuid>&suggestion=<uuid>`), or one deal's brief on Artifacts or in Operations → Deals, with Brief changes
 *   open over it or not (`briefPageBack`), or a group of past videos with the video she was on (`pastVideosBack`:
 *   "Back to Artifacts"). A full URL, `//…` and every other path are dropped (`backOf`).
 * - `scope`: Your numbers only — the Who we look for switch starts on this scope: `company` or a campaign's program id
 *   (checked against her campaigns by the card, CD13).
 * - `slack` / `reason`: Integrations only — Slack's answer to "Add to Slack" (the API's OAuth callback sends her back to
 *   `?section=integrations&slack=connected`, or `&slack=error&reason=…`). The Slack row says it.
 *
 * `card`, `country`, `document`, `fact`, `campaign`, `rule`, `edit`, `agency`, `back`, `scope` and `slack` are asked once: the page remembers the scope (`scope-memory.ts`), Integrations
 * keeps Slack's answer (`use-slack-outcome.ts`), the page keeps the door and its way back for the visit, and all take them out of the address, so a reload or Back never
 * re-asks them. A plain validator rather than zod on purpose: route `validateSearch` functions ship in the entry
 * chunk, which the sign-in page's budget pays for (and for the same reason nothing here imports `@/services/slack`).
 */
import { ARTIFACT_TABS } from '@/features/artifacts/tabs';
import { campaignDetailsHref, detailsBackOf, isDetailsCard } from '@/features/visibility/campaign-details/campaign-details-href';

/** Your numbers' cards a door may name. */
export const NUMBERS_CARDS = ['target', 'tiers'] as const;
export type NumbersCard = (typeof NUMBERS_CARDS)[number];
/** Draper Knowledge Base's cards a door may name (Review Content's and Campaign details' pointers land on them). */
export const DRAPER_CARDS = ['company-rules', 'product-facts', 'wording', 'negotiators'] as const;
export type DraperCard = (typeof DRAPER_CARDS)[number];
/** Team & roles' cards a door may name: the Agencies card. */
export const TEAM_CARDS = ['agencies'] as const;
export type TeamCard = (typeof TEAM_CARDS)[number];
/** Integrations' cards a door may name: Approvals in Slack. */
export const INTEGRATIONS_CARDS = ['approvals'] as const;
export type IntegrationsCard = (typeof INTEGRATIONS_CARDS)[number];
export const SETTINGS_CARDS = [...NUMBERS_CARDS, ...DRAPER_CARDS, ...TEAM_CARDS, ...INTEGRATIONS_CARDS] as const;
export type SettingsCard = NumbersCard | DraperCard | TeamCard | IntegrationsCard;

/**
 * The countries a door to Wording by country may name: services/target-markets.ts `TARGET_COUNTRIES`, written out here
 * so the entry chunk doesn't pull in that service (zod, the API client); settings-search.test.ts holds the two equal.
 */
export const WORDING_DOOR_COUNTRIES = ['US', 'CA', 'GB', 'AU', 'NZ', 'DE', 'FR', 'ES', 'IT', 'NL', 'SE', 'NO', 'DK', 'KR', 'BR', 'MX', 'IN', 'SG', 'PL'] as const;
const isWordingCountry = (value: unknown): value is string => WORDING_DOOR_COUNTRIES.some((c) => c === value);

/** A product facts document's id, as the server writes it (a company rule's id has the same shape). */
const DOCUMENT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isDocumentId = (value: unknown): value is string => typeof value === 'string' && DOCUMENT_ID.test(value);

/** Where Settings' way back may go, and the page it names. */
export type BackPage = 'Review Content' | 'Campaign details' | 'Home' | 'Artifacts' | 'Deals';
export interface SettingsBack {
  /** An address inside the app (never a full URL). */
  to: string;
  page: BackPage;
  /**
   * Brief changes, reopened over the page on this brief with this suggestion ringed: Home's (company rules round 5,
   * F2b), or over Artifacts' or Deals' brief (round 6's F1).
   */
  brief?: { briefId: string; suggestionId: string };
}

/**
 * The control Campaign details puts the focus back on after "Back to Campaign details" (company rules round 5, F2a):
 * `change` its "Change ›", `add` its "Add a rule for this campaign ›", `also` the link in "Draper also checks videos
 * here against …".
 */
export const DETAILS_FROM = ['change', 'add', 'also'] as const;
export type DetailsFrom = (typeof DETAILS_FROM)[number];
export const isDetailsFrom = (value: unknown): value is DetailsFrom => DETAILS_FROM.some((f) => f === value);

/** The longest way back kept (a draft's address is far shorter). */
const BACK_MAX = 1024;
/** Review Content on one draft: its path, and any search without spaces, a fragment or a backslash. */
const REVIEW_CONTENT_BACK = /^\/review\/content(\?[^\s#\\]*)?$/;

/** Campaign details' door may say these, and nothing else. */
const DETAILS_KEYS: ReadonlySet<string> = new Set(['playbook', 'details', 'card', 'back', 'from']);

/** A query value as the address wrote it, decoded; null when it doesn't decode. */
function decoded(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

/** Home with Brief changes open on one brief and one suggestion, exactly: `/?brief=<uuid>&suggestion=<uuid>` (F2b). */
const HOME_BRIEF_BACK = /^\/\?brief=([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})&suggestion=([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/**
 * One deal's brief, as a page's address names it (company rules round 6, F1): the keys each page may carry — the ones
 * kept, then the ones dropped (a search, a phase, a road stop, a pick on Focus view: Back reopens the same brief without
 * that filter) — `brief` and `suggestion` when Brief changes is open over it, and `from` (round 7's N1: `from=rules`,
 * the brief's "XERO’s company rules" link she followed, which the brief puts the focus back on).
 */
const BRIEF_PAGES: Readonly<Record<string, { page: 'Artifacts' | 'Deals'; keys: ReadonlySet<string> }>> = {
  '/artifacts': { page: 'Artifacts', keys: new Set(['kind', 'playbook', 'id', 'part', 'q', 'step', 'brief', 'suggestion', 'from']) },
  '/operations/deals': { page: 'Deals', keys: new Set(['view', 'playbook', 'doc', 'pick', 'at', 'brief', 'suggestion', 'from']) },
};

/**
 * The control a deal's brief puts the focus back on after "Back to Artifacts", "Back to Deals" or the browser's Back
 * (round 7's N1, as Campaign details' `from`): `rules`, the "Settings → Draper Knowledge Base" link of its company rules
 * card.
 */
export const BRIEF_FROM_RULES = 'rules';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** A whole positive id as an address writes it (no sign, no leading zero, safe), or undefined. */
const wholeIdOf = (value: string | undefined): string | undefined =>
  value !== undefined && /^[1-9]\d{0,15}$/.test(value) && Number.isSafeInteger(Number(value)) ? value : undefined;

/**
 * Artifacts on one brand deal's brief (`/artifacts?id=brand_deal:<n>&part=brief`, on any tab and campaign) or Operations
 * → Deals with one deal's brief open (`/operations/deals?view=list|focus&doc=brief:<n>`, on any campaign), each with
 * `from=rules` when she followed its company rules link (round 7's N1) and `brief=<uuid>&suggestion=<uuid>` when Brief
 * changes is open over it (company rules round 6, F1). The app writes these
 * addresses encoded (`brand_deal%3A197`), so the query is read decoded, in any order, each key once and only the page's
 * own; each value is checked, and the answer is rebuilt in one canonical encoded form. Pure.
 */
function briefPageBack(value: string): SettingsBack | undefined {
  const at = value.indexOf('?');
  const shape = at > 0 ? BRIEF_PAGES[value.slice(0, at)] : undefined;
  if (!shape || /[\s#\\]/.test(value)) return undefined;
  const asked = new Map<string, string>();
  for (const [key, v] of new URLSearchParams(value.slice(at + 1))) {
    if (asked.has(key) || !shape.keys.has(key)) return undefined;
    asked.set(key, v);
  }
  const playbook = asked.get('playbook');
  if (playbook !== undefined && !wholeIdOf(playbook)) return undefined;
  const from = asked.get('from');
  if (from !== undefined && from !== BRIEF_FROM_RULES) return undefined;
  const [briefId, suggestionId] = [asked.get('brief'), asked.get('suggestion')];
  if ((briefId === undefined) !== (suggestionId === undefined)) return undefined;
  if (briefId !== undefined && !(UUID.test(briefId) && UUID.test(suggestionId ?? ''))) return undefined;
  const out = new URLSearchParams();
  if (shape.page === 'Artifacts') {
    const kind = asked.get('kind');
    if (kind !== undefined && (kind === 'all' || !ARTIFACT_TABS.some((tab) => tab === kind))) return undefined;
    const deal = wholeIdOf(/^brand_deal:(.*)$/.exec(asked.get('id') ?? '')?.[1]);
    if (!deal || asked.get('part') !== 'brief') return undefined;
    if (kind !== undefined) out.set('kind', kind);
    if (playbook !== undefined) out.set('playbook', playbook);
    out.set('id', `brand_deal:${deal}`);
    out.set('part', 'brief');
  } else {
    const view = asked.get('view') ?? 'list';
    const deal = wholeIdOf(/^brief:(.*)$/.exec(asked.get('doc') ?? '')?.[1]);
    if ((view !== 'list' && view !== 'focus') || !deal) return undefined;
    out.set('view', view);
    if (playbook !== undefined) out.set('playbook', playbook);
    out.set('doc', `brief:${deal}`);
  }
  if (from !== undefined) out.set('from', from);
  const brief = briefId !== undefined && suggestionId !== undefined ? { briefId: briefId.toLowerCase(), suggestionId: suggestionId.toLowerCase() } : undefined;
  if (brief) {
    out.set('brief', brief.briefId);
    out.set('suggestion', brief.suggestionId);
  }
  return { to: `${value.slice(0, at)}?${out.toString()}`, page: shape.page, ...(brief ? { brief } : {}) };
}

/** A group of past videos' page, by its id: `/past-videos/<uuid>`, then its search or nothing. */
const PAST_VIDEOS_PAGE = /^\/past-videos\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})(?:\?(.*))?$/;
/** The group page's own keys a way back keeps, in the order it is rebuilt: the check, the one compared, the video. */
const PAST_VIDEOS_IDS = ['run', 'against', 'video'] as const;
const PAST_VIDEOS_VIEW = 'points';
/**
 * `against`'s word for the first check (services/past-videos.ts `PAST_VIDEO_FIRST`, written out here so the entry chunk
 * doesn't pull in that service): a check again compared with the first check reads `?run=<uuid>&against=first`.
 */
const PAST_VIDEOS_FIRST = 'first';

/**
 * A group of past videos (lane 3's past videos round 7, S1; with lane 1's OK): a company rule's row in a past video's
 * panel names "Settings → Draper Knowledge Base", and Settings leads back to that video in its group, named as the page
 * it belongs to, "Artifacts" (the group page's own way back is "← Artifacts"; "Past videos" isn't one of Lucid's page
 * names). `/past-videos/<uuid>` with only the group page's own keys: `run`, `against` and `video` (each a uuid;
 * `against` may also be `first`, the first check) and `view=points`, in any order, each once, read decoded (as
 * `briefPageBack` reads its keys). Any other key (the page's
 * read-once `focus` and `answer` included), a value of another shape, a fragment, a full URL and every other path are
 * dropped. Rebuilt in one canonical encoded form, the ids lower-cased. Pure.
 */
function pastVideosBack(value: string): SettingsBack | undefined {
  const page = PAST_VIDEOS_PAGE.exec(value);
  if (!page || /[\s#\\]/.test(value)) return undefined;
  const asked = new Map<string, string>();
  for (const [key, v] of new URLSearchParams(page[2] ?? '')) {
    const own = PAST_VIDEOS_IDS.some((k) => k === key) || key === 'view';
    if (asked.has(key) || !own) return undefined;
    asked.set(key, v);
  }
  const out = new URLSearchParams();
  for (const key of PAST_VIDEOS_IDS) {
    const id = asked.get(key);
    if (id === undefined) continue;
    if (key === 'against' && id === PAST_VIDEOS_FIRST) out.set(key, id);
    else if (UUID.test(id)) out.set(key, id.toLowerCase());
    else return undefined;
  }
  const view = asked.get('view');
  if (view !== undefined && view !== PAST_VIDEOS_VIEW) return undefined;
  if (view !== undefined) out.set('view', view);
  const search = out.toString();
  return { to: `/past-videos/${page[1]!.toLowerCase()}${search ? `?${search}` : ''}`, page: 'Artifacts' };
}

/**
 * The way back a link may ask Settings for (F1), or undefined: Review Content's address as it was (the draft she was
 * on); Campaign details' door — `/operations` with a whole `playbook` and `details=true`, and at most its own `card`
 * (approvals), way back (`detailsBackOf`: Review or Settings) and `from=change|add|also`, the control it
 * puts the focus back on, nothing else — written the one way Operations reads it (`campaignDetailsHref`, then `from`);
 * or Home's Brief changes on one brief and one suggestion (exactly `/?brief=<uuid>&suggestion=<uuid>`, in
 * that order), named "Home"; or one deal's brief on Artifacts or in Operations → Deals (`briefPageBack`),
 * named "Artifacts" or "Deals"; or a group of past videos (`pastVideosBack`), named "Artifacts".
 * A full URL, `//…`, `/settings` and every other path are dropped. Pure.
 */
export function backOf(value: unknown): SettingsBack | undefined {
  if (typeof value !== 'string' || value.length > BACK_MAX || !value.startsWith('/') || value.startsWith('//')) return undefined;
  if (REVIEW_CONTENT_BACK.test(value)) return { to: value, page: 'Review Content' };
  const briefPage = briefPageBack(value);
  if (briefPage) return briefPage;
  const pastVideos = pastVideosBack(value);
  if (pastVideos) return pastVideos;
  const home = HOME_BRIEF_BACK.exec(value);
  if (home) {
    const [briefId, suggestionId] = [home[1]!.toLowerCase(), home[2]!.toLowerCase()];
    return { to: `/?brief=${briefId}&suggestion=${suggestionId}`, page: 'Home', brief: { briefId, suggestionId } };
  }
  const at = value.indexOf('?');
  if (at < 0 || value.slice(0, at) !== '/operations') return undefined;
  const query = value.slice(at + 1);
  if (!/^[A-Za-z0-9=&%._~-]+$/.test(query)) return undefined;
  const pairs = query.split('&').map((pair) => pair.split('='));
  if (pairs.some((pair) => pair.length !== 2)) return undefined;
  const asked = new Map(pairs.map(([key, v]) => [key ?? '', v ?? ''] as const));
  if (asked.size !== pairs.length || [...asked.keys()].some((key) => !DETAILS_KEYS.has(key))) return undefined;
  const playbook = asked.get('playbook') ?? '';
  if (asked.get('details') !== 'true' || !/^[1-9]\d{0,15}$/.test(playbook) || !Number.isSafeInteger(Number(playbook))) return undefined;
  const card = asked.get('card');
  if (card !== undefined && !isDetailsCard(card)) return undefined;
  const from = asked.get('from');
  if (from !== undefined && !isDetailsFrom(from)) return undefined;
  const rawBack = asked.get('back');
  const back = rawBack === undefined ? undefined : detailsBackOf(decoded(rawBack));
  if (rawBack !== undefined && !back) return undefined;
  const href = campaignDetailsHref(Number(playbook), { back: back?.to, card: card ?? null });
  return { to: from ? `${href}&from=${from}` : href, page: 'Campaign details' };
}

/** Who we look for's scope: the company ("All of Xero") or one campaign, by its program id. */
export type ScopeChoice = 'company' | number;

/** Slack's answer to "Add to Slack": the API's OAuth callback sends her back to `?section=integrations&slack=…` (G11: said once). */
export const SLACK_RESULTS = ['connected', 'error'] as const;
export type SlackResult = (typeof SLACK_RESULTS)[number];
/** Why it failed, as the callback names it. Any other reason is dropped (the row then says it plainly). */
export const SLACK_ERROR_REASONS = ['access_denied', 'state', 'forbidden', 'exchange', 'not_configured'] as const;
export type SlackErrorReason = (typeof SLACK_ERROR_REASONS)[number];

export interface SettingsSearch {
  section: string;
  card?: SettingsCard;
  /** With `card: 'wording'`: the country whose row the door lands on; with `card: 'company-rules'`: the country its box opens on. */
  country?: string;
  /** With `card: 'product-facts'` only: the document whose facts the door opens. */
  document?: string;
  /** With `card: 'product-facts'` and a `document` only: the fact See the facts lands on. */
  fact?: string;
  /** With `card: 'company-rules'` only: the campaign the box opens on (C1). */
  campaign?: number;
  /** With `card: 'company-rules'` only: the rule the door lands on (round 4's F1). */
  rule?: string;
  /** With `card: 'wording'` and a `country` only: that country's row opens in its editor (round 4's F1). */
  edit?: true;
  /** With `card: 'agencies'` only: the agency whose row the door lands on. */
  agency?: number;
  /** The page she came from (`backOf`), as the address wrote it. */
  back?: string;
  scope?: ScopeChoice;
  /** Integrations only: Slack's answer, asked once. */
  slack?: SlackResult;
  /** With `slack: 'error'` only. */
  reason?: SlackErrorReason;
}

/** What a link may pass. */
export interface SettingsSearchInput {
  section?: string;
  card?: SettingsCard;
  country?: string;
  document?: string;
  fact?: string;
  campaign?: number | string;
  rule?: string;
  edit?: boolean | string;
  agency?: number | string;
  back?: string;
  scope?: ScopeChoice | string;
  slack?: SlackResult | string;
  reason?: SlackErrorReason | string;
}

export const isNumbersCard = (value: unknown): value is NumbersCard => NUMBERS_CARDS.some((c) => c === value);
export const isDraperCard = (value: unknown): value is DraperCard => DRAPER_CARDS.some((c) => c === value);
export const isTeamCard = (value: unknown): value is TeamCard => TEAM_CARDS.some((c) => c === value);
export const isIntegrationsCard = (value: unknown): value is IntegrationsCard => INTEGRATIONS_CARDS.some((c) => c === value);
const isSlackResult = (value: unknown): value is SlackResult => SLACK_RESULTS.some((r) => r === value);
const isSlackReason = (value: unknown): value is SlackErrorReason => SLACK_ERROR_REASONS.some((r) => r === value);

/** A whole positive id (a number, or its digits); anything else undefined. */
export function positiveId(value: unknown): number | undefined {
  const n = typeof value === 'number' ? value : typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : Number.NaN;
  return Number.isSafeInteger(n) && n > 0 ? n : undefined;
}

/** 'company', or a whole positive program id (a number, or its digits). */
export function scopeChoice(value: unknown): ScopeChoice | undefined {
  if (value === 'company') return 'company';
  const n = typeof value === 'number' ? value : typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : Number.NaN;
  return Number.isSafeInteger(n) && n > 0 ? n : undefined;
}

export function parseSettingsSearch(input: SettingsSearchInput | Record<string, unknown>): SettingsSearch {
  const raw = input as Record<string, unknown>;
  // Every key is answered, a dropped one as undefined: the router lays this answer over the address's own search
  // (router-core's `{ ...parentSearch, ...validated }`), so a key left out would reach the page as it was typed.
  const out: SettingsSearch = {
    section: typeof raw.section === 'string' && raw.section ? raw.section : 'account',
    card: undefined,
    country: undefined,
    document: undefined,
    fact: undefined,
    campaign: undefined,
    rule: undefined,
    edit: undefined,
    back: backOf(raw.back)?.to,
    scope: undefined,
    slack: undefined,
    reason: undefined,
    agency: undefined,
  };
  // Slack's answer only means something on Integrations; so does its Approvals in Slack card.
  if (out.section === 'integrations') {
    if (isIntegrationsCard(raw.card)) out.card = raw.card;
    if (isSlackResult(raw.slack)) {
      out.slack = raw.slack;
      if (raw.slack === 'error' && isSlackReason(raw.reason)) out.reason = raw.reason;
    }
    return out;
  }
  // Draper Knowledge Base takes one of its own cards, and each card only the places it has; nothing else.
  if (out.section === 'draper') {
    if (isDraperCard(raw.card)) out.card = raw.card;
    if (out.card === 'wording' && isWordingCountry(raw.country)) out.country = raw.country;
    if (out.card === 'product-facts' && isDocumentId(raw.document)) out.document = raw.document.toLowerCase();
    if (out.document && isDocumentId(raw.fact)) out.fact = raw.fact.toLowerCase();
    if (out.card === 'company-rules') {
      const campaign = scopeChoice(raw.campaign);
      if (typeof campaign === 'number') out.campaign = campaign;
    }
    // Round 4's F1: the rule a door lands on, the country Company rules' box opens on, and Wording by country's editor.
    if (out.card === 'company-rules' && isDocumentId(raw.rule)) out.rule = raw.rule.toLowerCase();
    if (out.card === 'company-rules' && isWordingCountry(raw.country)) out.country = raw.country;
    if (out.card === 'wording' && out.country && (raw.edit === true || raw.edit === 'true')) out.edit = true;
    return out;
  }
  // Team & roles takes its Agencies card, and on it one agency's row, and nothing else.
  if (out.section === 'team') {
    if (isTeamCard(raw.card)) out.card = raw.card;
    if (out.card === 'agencies') out.agency = positiveId(raw.agency);
    return out;
  }
  // A card and a scope only mean something on Your numbers.
  if (out.section !== 'numbers') return out;
  if (isNumbersCard(raw.card)) out.card = raw.card;
  const scope = scopeChoice(raw.scope);
  if (scope !== undefined) out.scope = scope;
  return out;
}

/** The same address without its one-time asks (G11). */
export const withoutDoor = ({ section }: SettingsSearch): SettingsSearch => ({ section });
